import { computed } from 'vue';
import HaspPage, { HaspTemplatePage } from '../Hasp/HaspPage';

/**
 * Provides reactive access to pages and objects in the current design.
 * @param {Ref} layer        - HaspObjectLayer ref
 * @param {Ref<number>} tick - increment to force recomputation after Konva mutations
 */
export function useHaspObjects(layer, tick = null) {
    const pages = computed(() => {
        tick?.value; // reactive dependency so callers can force refresh
        if (!layer?.value) return [];
        return layer.value
            .getChildren()
            .filter(c => c instanceof HaspPage && c.attrs.haspid >= 0 && c.obj !== 'template_page')
            .sort((a, b) => a.attrs.haspid - b.attrs.haspid)
            .map(p => ({
                id: p.attrs.haspid,
                label: `P${p.attrs.haspid}${p.attrs.comment ? ' — ' + p.attrs.comment : ''}`,
            }));
    });

    const templatePages = computed(() => {
        tick?.value;
        if (!layer?.value) return [];
        return layer.value
            .getChildren()
            .filter(c => c instanceof HaspTemplatePage)
            .map(p => ({
                id: p.attrs.haspid,
                label: p.attrs.comment ?? `Template P${p.attrs.haspid}`,
                objectCount: p.getChildren().filter(c => c.haspid !== undefined).length,
            }));
    });

    function objectsOnPage(pageId) {
        tick?.value;
        if (!layer?.value) return [];
        const page = layer.value
            .getChildren()
            .find(c => c instanceof HaspPage && c.attrs.haspid === pageId);
        if (!page) return [];
        return page
            .getChildren()
            .filter(c => c.haspid !== undefined && c.obj !== 'page')
            .map(c => ({
                id: c.haspid,
                obj: c.obj,
                label: `b${c.haspid}${c.attrs?.comment ? ' — ' + c.attrs.comment : ''} (${c.obj})`,
            }));
    }

    // Properties returned as { name, type } objects.
    // type 'color' triggers a color-picker in the flow editor.
    function propertiesForObject(pageId, objectId) {
        tick?.value;
        if (!layer?.value) return [];
        const page = layer.value
            .getChildren()
            .find(c => c instanceof HaspPage && c.attrs.haspid === pageId);
        if (!page) return [];
        const obj = page.getChildren().find(c => c.haspid === objectId);
        if (!obj) return [];
        return [
            { name: 'text',          type: 'text' },
            { name: 'val',           type: 'number' },
            { name: 'color',         type: 'color' },
            { name: 'bg_color',      type: 'color' },
            { name: 'text_color',    type: 'color' },
            { name: 'border_color',  type: 'color' },
            { name: 'shadow_color',  type: 'color' },
            { name: 'src',           type: 'text' },
            { name: 'hidden',        type: 'boolean' },
            { name: 'enabled',       type: 'boolean' },
            { name: 'opacity',       type: 'number' },
        ];
    }

    return { pages, templatePages, objectsOnPage, propertiesForObject };
}
